import { useState } from 'react';
import { Bell, Copy, FilePlus2, MessageCircle, RefreshCw, Send, XCircle } from 'lucide-react';
import { Selo } from '@/components/common/ListaMobile';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/common/Modal';
import { cn } from '@/lib/utils';
import {
  useAtualizarRecorrencia,
  useCancelarAssinatura,
  useGerarFatura,
  useReenviarFatura,
  useRecorrencia,
} from '@/hooks/useRecorrencia';
import type {
  Fatura,
  MensagemCobranca,
  ModoCobranca,
  StatusFatura,
} from '@/types/recorrencia.types';

const VISIVEIS = 4;

const MODO_LABEL: Record<ModoCobranca, string> = {
  manual_link: 'Automática por link',
  cartao_auto: 'Cartão automático',
  nenhum: 'Desligada',
};

const FATURA_TOM: Record<
  StatusFatura,
  { rotulo: string; tom: 'sucesso' | 'erro' | 'aviso' | 'neutro' }
> = {
  paga: { rotulo: 'Paga', tom: 'sucesso' },
  aberta: { rotulo: 'Em aberto', tom: 'aviso' },
  vencida: { rotulo: 'Vencida', tom: 'erro' },
  cancelada: { rotulo: 'Cancelada', tom: 'neutro' },
};

const AVISO_LABEL: Record<string, string> = {
  cobranca_nova: 'Fatura disponível',
  cobranca_lembrete: 'Lembrete',
  cobranca_vencida: 'Fatura vencida',
  acesso_suspenso: 'Acesso suspenso',
  pagamento_confirmado: 'Pagamento confirmado',
  cancelamento_confirmado: 'Cancelamento',
  reembolso_confirmado: 'Reembolso',
  cartao_falhou: 'Cartão recusado',
};

const ENTREGA: Record<
  MensagemCobranca['status'],
  { rotulo: string; tom: 'sucesso' | 'erro' | 'aviso' | 'info' | 'neutro' }
> = {
  pendente: { rotulo: 'Na fila', tom: 'neutro' },
  enviando: { rotulo: 'Enviando', tom: 'neutro' },
  enviada: { rotulo: 'Enviada', tom: 'info' },
  entregue: { rotulo: 'Entregue', tom: 'sucesso' },
  lida: { rotulo: 'Lida', tom: 'sucesso' },
  falhou: { rotulo: 'Falhou', tom: 'erro' },
  cancelada: { rotulo: 'Cancelada', tom: 'neutro' },
};

const reais = (centavos: number) =>
  (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const diaBr = (dia: string | null) => {
  if (!dia) return '-';
  const [a, m, d] = dia.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
};
const quando = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

function mensagemDeErro(error: unknown): string {
  const e = error as { response?: { data?: { message?: string } } };
  return e?.response?.data?.message || 'Não deu certo. Tente de novo.';
}

/** Faturas, avisos e ações da cobrança recorrente de um produtor (área finance). */
export function CobrancaRecorrenteSection({ userId }: { userId: number }) {
  const { data, isLoading, isError } = useRecorrencia(userId);
  const gerar = useGerarFatura(userId);
  const reenviar = useReenviarFatura(userId);
  const atualizar = useAtualizarRecorrencia(userId);
  const [verTodas, setVerTodas] = useState(false);
  const [cancelarAberto, setCancelarAberto] = useState(false);
  const [aviso, setAviso] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);

  const executar = async (acao: () => Promise<unknown>, ok: string) => {
    setAviso(null);
    try {
      await acao();
      setAviso({ tipo: 'ok', texto: ok });
    } catch (error) {
      setAviso({ tipo: 'erro', texto: mensagemDeErro(error) });
    }
  };

  const copiar = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setAviso({ tipo: 'ok', texto: 'Link copiado.' });
    } catch {
      setAviso({ tipo: 'erro', texto: 'Não deu para copiar. Abra a fatura e copie o link.' });
    }
  };

  if (isLoading) {
    return (
      <section aria-busy="true">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-secondary">
          Cobrança recorrente
        </h3>
        <div className="h-24 animate-pulse rounded-xl bg-muted" />
      </section>
    );
  }
  if (isError || !data) {
    return (
      <section>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-secondary">
          Cobrança recorrente
        </h3>
        <p className="rounded-xl border border-dashed border-border p-4 text-sm text-text-secondary">
          Não deu para carregar a cobrança recorrente.
        </p>
      </section>
    );
  }

  const { assinatura, faturas, mensagens, preferencias } = data;
  const faturasVisiveis = verTodas ? faturas : faturas.slice(0, VISIVEIS);
  const podeGerar =
    assinatura && assinatura.ciclo && !['store_ios', 'store_android'].includes(assinatura.origem);
  const podeCancelar =
    assinatura &&
    !['store_ios', 'store_android'].includes(assinatura.origem) &&
    assinatura.status !== 'expired';

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
          Cobrança recorrente
        </h3>
        {assinatura && (
          <Selo tom={assinatura.modo_cobranca === 'nenhum' ? 'neutro' : 'info'}>
            {MODO_LABEL[assinatura.modo_cobranca]}
          </Selo>
        )}
      </div>

      <div className="space-y-3 rounded-xl border border-border bg-card p-4 shadow-card">
        {!data.ativa && (
          <p className="text-xs text-text-secondary">
            A cobrança automática está desligada neste ambiente. As ações abaixo funcionam, mas os
            avisos não saem sozinhos.
          </p>
        )}
        {assinatura?.cancelar_no_fim && (
          <p className="rounded-lg bg-warning/10 px-3 py-2 text-sm text-warning">
            Cancelamento pedido: o acesso vai até {diaBr(assinatura.current_period_end)} e não serão
            geradas novas faturas.
          </p>
        )}
        {aviso && (
          <p
            role="status"
            className={cn(
              'rounded-lg px-3 py-2 text-sm',
              aviso.tipo === 'ok' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
            )}
          >
            {aviso.texto}
          </p>
        )}

        {/* Faturas */}
        {faturas.length === 0 ? (
          <p className="text-sm text-text-secondary">
            Nenhuma fatura ainda. A primeira abre 5 dias antes do vencimento.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {faturasVisiveis.map((f: Fatura) => (
              <li key={f.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text-title">
                    {diaBr(f.periodo_inicio)} a {diaBr(f.periodo_fim)}
                  </p>
                  <p className="text-xs text-text-secondary">
                    {reais(f.valor_centavos)} · vence {diaBr(f.vencimento)}
                    {f.baixa_manual_forma ? ' · baixa manual' : ''}
                  </p>
                </div>
                <Selo tom={FATURA_TOM[f.status].tom}>{FATURA_TOM[f.status].rotulo}</Selo>
                {f.pagamento_duplicado_em && <Selo tom="erro">Pago em dobro</Selo>}
                {(f.status === 'aberta' || f.status === 'vencida') && (
                  <div className="flex gap-1">
                    {f.link_pagamento && (
                      <button
                        type="button"
                        onClick={() => copiar(f.link_pagamento as string)}
                        className="rounded-md p-1.5 text-text-secondary hover:bg-muted hover:text-text-title"
                        aria-label="Copiar link de pagamento"
                        title="Copiar link"
                      >
                        <Copy size={15} />
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={reenviar.isPending}
                      onClick={() =>
                        executar(
                          () => reenviar.mutateAsync(f.id),
                          'Link reenviado. Sai na próxima janela de envio.'
                        )
                      }
                      className="rounded-md p-1.5 text-text-secondary hover:bg-muted hover:text-text-title disabled:opacity-50"
                      aria-label="Reenviar link de pagamento"
                      title="Reenviar link"
                    >
                      <Send size={15} />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {faturas.length > VISIVEIS && (
          <button
            type="button"
            onClick={() => setVerTodas((v) => !v)}
            className="text-xs font-semibold text-primary"
          >
            {verTodas ? 'Mostrar menos' : `Ver as ${faturas.length} faturas`}
          </button>
        )}

        {/* Avisos */}
        {mensagens.length > 0 && (
          <div>
            <p className="mb-1.5 text-xs font-medium text-text-secondary">Últimos avisos</p>
            <ul className="space-y-1">
              {mensagens.slice(0, 5).map((m) => (
                <li key={m.id} className="flex items-center gap-2 text-xs">
                  {m.canal === 'whatsapp' ? (
                    <MessageCircle
                      size={13}
                      className="shrink-0 text-text-hint"
                      aria-label="WhatsApp"
                    />
                  ) : (
                    <Bell size={13} className="shrink-0 text-text-hint" aria-label="Push" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-text-body">
                    {AVISO_LABEL[m.template] ?? m.template}
                  </span>
                  <span className="tabular-nums text-text-hint">{quando(m.created_at)}</span>
                  <Selo tom={ENTREGA[m.status].tom}>{ENTREGA[m.status].rotulo}</Selo>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* WhatsApp */}
        <label className="flex items-center justify-between gap-3 rounded-lg bg-muted px-3 py-2">
          <span className="text-sm text-text-title">
            Avisos de cobrança pelo WhatsApp
            {!preferencias.whatsapp_cobranca && preferencias.optout_origem === 'resposta_sair' && (
              <span className="block text-xs text-text-secondary">
                O cliente respondeu SAIR. Recebe só pelo app.
              </span>
            )}
          </span>
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={preferencias.whatsapp_cobranca}
            disabled={atualizar.isPending}
            onChange={(e) =>
              executar(
                () => atualizar.mutateAsync({ whatsapp_cobranca: e.target.checked }),
                e.target.checked
                  ? 'Avisos pelo WhatsApp ligados.'
                  : 'Avisos pelo WhatsApp desligados.'
              )
            }
          />
        </label>

        {/* Ações */}
        <div className="flex flex-wrap gap-2">
          {podeGerar && (
            <Button
              size="sm"
              variant="secondary"
              disabled={gerar.isPending}
              onClick={() =>
                executar(() => gerar.mutateAsync(), 'Fatura gerada e link enviado ao produtor.')
              }
            >
              {gerar.isPending ? (
                <RefreshCw size={15} className="animate-spin" />
              ) : (
                <FilePlus2 size={15} />
              )}
              Gerar fatura agora
            </Button>
          )}
          {podeCancelar && (
            <Button
              size="sm"
              variant="secondary"
              className="text-error"
              onClick={() => setCancelarAberto(true)}
            >
              <XCircle size={15} />
              Cancelar assinatura
            </Button>
          )}
        </div>
      </div>

      <CancelarAssinaturaModal
        userId={userId}
        aberto={cancelarAberto}
        acessoAte={assinatura?.current_period_end ?? null}
        onClose={() => setCancelarAberto(false)}
        onCancelada={(texto) => {
          setCancelarAberto(false);
          setAviso({ tipo: 'ok', texto });
        }}
      />
    </section>
  );
}

function CancelarAssinaturaModal({
  userId,
  aberto,
  acessoAte,
  onClose,
  onCancelada,
}: {
  userId: number;
  aberto: boolean;
  acessoAte: string | null;
  onClose: () => void;
  onCancelada: (texto: string) => void;
}) {
  const cancelar = useCancelarAssinatura(userId);
  const [imediato, setImediato] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const confirmar = async () => {
    setErro(null);
    if (motivo.trim().length < 3) {
      setErro('Informe o motivo do cancelamento.');
      return;
    }
    try {
      await cancelar.mutateAsync({ imediato, motivo: motivo.trim() });
      onCancelada(
        imediato
          ? 'Assinatura cancelada. O acesso foi bloqueado agora.'
          : `Cancelamento registrado. Acesso até ${diaBr(acessoAte)}.`
      );
      setMotivo('');
      setImediato(false);
    } catch (error) {
      setErro(mensagemDeErro(error));
    }
  };

  return (
    <Modal isOpen={aberto} onClose={onClose} title="Cancelar assinatura" size="sm">
      <div className="space-y-4">
        <fieldset className="space-y-2">
          <legend className="mb-1 text-sm font-medium text-text-title">Quando</legend>
          <label className="flex items-start gap-2 text-sm text-text-body">
            <input
              type="radio"
              name="quando"
              className="mt-1 accent-primary"
              checked={!imediato}
              onChange={() => setImediato(false)}
            />
            <span>
              No fim do período
              <span className="block text-xs text-text-secondary">
                Acesso até {diaBr(acessoAte)}. Nenhuma fatura nova.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-text-body">
            <input
              type="radio"
              name="quando"
              className="mt-1 accent-primary"
              checked={imediato}
              onChange={() => setImediato(true)}
            />
            <span>
              Agora
              <span className="block text-xs text-text-secondary">O app bloqueia na hora.</span>
            </span>
          </label>
        </fieldset>
        <div>
          <label
            htmlFor="cancelar-motivo"
            className="mb-1.5 block text-xs font-medium text-text-secondary"
          >
            Motivo
          </label>
          <textarea
            id="cancelar-motivo"
            rows={2}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            onBlur={() =>
              motivo && motivo.trim().length < 3 && setErro('Informe o motivo do cancelamento.')
            }
            className="w-full rounded-lg border border-border-strong bg-muted px-3 py-2 text-sm text-text-title focus:border-primary focus:outline-none"
            placeholder="Ex.: vendeu a fazenda"
            aria-invalid={Boolean(erro)}
          />
        </div>
        {erro && <p className="text-sm text-error">{erro}</p>}
        <p className="text-xs text-text-secondary">
          O produtor recebe a confirmação do cancelamento.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={cancelar.isPending}>
            Voltar
          </Button>
          <Button
            onClick={confirmar}
            disabled={cancelar.isPending}
            className="bg-error text-white hover:bg-error/90"
          >
            {cancelar.isPending ? 'Cancelando...' : 'Cancelar assinatura'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
